import FilteredList from './filtered-list.js.jsx'
import Query from './query.js.jsx'
import Table from './table.js.jsx'
import ErrorMessage from './error-message.js.jsx'
import { format as formatDate, parseISO } from 'date-fns'
import { differenceInCalendarDays } from 'date-fns/esm'
import { isoParse } from 'd3'
import Label from './ui/elements/label'
import Input from './ui/elements/input'
import Select from './ui/elements/select'
import ProjectLabel from './ui/shared/project-label'
import LinkButton from './ui/elements/link.js'
import bandOrders from '../band-orders.js'
import Button from './ui/elements/button.js'
import { useNotifications } from '../store/use-notifications'
import Notification from './ui/shared/notification'

export default function UserProjectsWrapper(props) {
  const {
    notifications,
    pushNotification,
    popNotification,
  } = useNotifications()

  return (
    <>
      <UserProjects {...props} pushNotification={pushNotification} />
      {notifications[0] && (
        <Notification
          notification={notifications[0]}
          onDismiss={popNotification}
        />
      )}
    </>
  )
}

class UserProjects extends FilteredList {
  constructor(props) {
    super(props)
    this.state = {
      search: null,
      sort: { id: 'created_at', dir: 'desc' },
      query: this.query.bind(this),
    }
  }

  plantCountsStatus(status) {
    switch (status) {
      case 'published':
        return 'Ready'
      case 'requested':
        return 'Processing'
      case 'bad_request':
        return 'Not available'
      default:
        return ''
    }
  }

  render() {
    return (
      <Query query={this.state.query}>
        {({ loading, data, error, refetch }) => {
          const message =
            this.state.message ||
            (error && {
              type: 'danger',
              html: (
                <div>
                  <strong>
                    An unexpected error occured. Technical details:
                  </strong>
                  <pre>
                    <code>{error.toString()}</code>
                  </pre>
                </div>
              ),
            })

          return (
            <div className="tw-absolute tw-top-0 tw-bottom-0 tw-w-full tw-pt-24 tw-flex tw-flex-col tw-px-9">
              {message && (
                <ErrorMessage key={error} message={message} timeout={10000} />
              )}
              <div className="tw-flex tw-flex-row tw-space-x-2 tw-bg-white tw-p-3 tw-bg-white tw-border tw-border-solid tw-border-system-grey-200 tw-rounded">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <Input
                    type="search"
                    id="search"
                    placeholder="Search"
                    // For some reason the search input is *slightly* off in
                    // height compared to other inputs without this padding
                    className="tw-pb-2.5"
                    onChange={this.updateFilter.bind(
                      this,
                      refetch,
                      'search',
                      true
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="filter-plant-counts">Plant Counts</Label>
                  <Select
                    id="filter-plant-counts"
                    onChange={this.updateFilter.bind(
                      this,
                      refetch,
                      'plantCounts',
                      false
                    )}>
                    <option value="">All</option>
                    <option value="requested">Processing</option>
                    <option value="published">Ready</option>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filter-bands">Bands</Label>
                  <Select
                    id="filter-bands"
                    onChange={this.updateFilter.bind(
                      this,
                      refetch,
                      'bands',
                      false
                    )}>
                    {bandOrders.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="filter-from">From</Label>
                  <Input
                    type="date"
                    id="filter-from"
                    onChange={this.updateFilter.bind(
                      this,
                      refetch,
                      'from',
                      false
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="filter-to">To</Label>
                  <Input
                    type="date"
                    id="filter-to"
                    onChange={this.updateFilter.bind(
                      this,
                      refetch,
                      'to',
                      false
                    )}
                  />
                </div>
              </div>

              {!error && (
                <>
                  <div className="tw-my-3">
                    Showing {data?.projects?.length || 0} of {data?.total || 0}{' '}
                    Uploads
                  </div>

                  <div className="tw-flex-1 tw-overflow-y-auto tw-mt-3 tw-mb-4">
                    <div className="projects-table-wrapper tw-bg-white tw-border tw-border-solid tw-border-system-grey-200 tw-rounded">
                      <Table
                        loading={loading}
                        data={data?.projects}
                        columns={[
                          {
                            id: 'id',
                            name: '',
                            fn: d => (
                              <ProjectLabel
                                number={d.id}
                                status={
                                  d.user_status !== 'success' && d.user_status
                                }
                                color={
                                  d.status === 'success'
                                    ? 'success'
                                    : d.status === 'fail_publish'
                                    ? 'danger'
                                    : 'warning'
                                }
                              />
                            ),
                          },
                          { id: 'farm', name: 'Farm', fn: d => d.farm_name },
                          { id: 'field', name: 'Field', fn: d => d.field_name },
                          {
                            id: 'survey_date',
                            name: 'Survey Date',
                            fn: d =>
                              d.survey_date &&
                              formatDate(parseISO(d.survey_date), 'yyyy-MM-dd'),
                          },
                          {
                            id: 'bands',
                            name: 'Bands',
                            fn: d => d.bands && d.bands.toUpperCase(),
                          },
                          {
                            id: 'images',
                            name: 'Images',
                            classFn: () => 'tw-text-right',
                            headerClassName: 'tw-text-right',
                            fn: d => d.photos_count,
                          },
                          {
                            id: 'plant_counts',
                            name: 'Plant Counts',
                            fn: d => this.plantCountsStatus(d.plant_counts),
                          },
                          {
                            name: '',
                            fn: d => (
                              <div className="tw-flex tw-flex-row tw-space-x-2">
                                {d.status === 'success' ? (
                                  <LinkButton
                                    href={this.props.apiClient.createUrl(
                                      'project',
                                      { id: d.id },
                                      { format: '' }
                                    )}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    View
                                  </LinkButton>
                                ) : d.status === 'uploading' &&
                                  differenceInCalendarDays(
                                    new Date(),
                                    isoParse(d.updated_at)
                                  ) <= 7 ? (
                                  <LinkButton
                                    href={`/projects/${d.id}/resume`}
                                    className="btn btn-default btn-xs"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    Resume upload
                                  </LinkButton>
                                ) : null}
                                {this.props.enableEdit && (
                                  <Button
                                    onClick={() => this.delete(d, refetch)}>
                                    Delete
                                  </Button>
                                )}
                              </div>
                            ),
                          },
                        ]}
                        sort={this.state.sort}
                        onSortChange={sort => {
                          this.setState(
                            { sort: { id: sort.col.id, dir: sort.dir } },
                            refetch
                          )
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        }}
      </Query>
    )
  }

  updateFilter(refetch, field, debounce, e) {
    this.setState({ [field]: e.target.value, message: null }, () => {
      if (debounce) {
        clearTimeout(this.filterTimer)
        this.filterTimer = setTimeout(refetch, 250)
      } else {
        refetch()
      }
    })
  }

  getSearchParams() {
    return ['search', 'plantCounts', 'bands', 'from', 'to', 'deleted']
  }

  delete(project, refetch) {
    const uploadName = `${project.field_name} from ${formatDate(
      parseISO(project.survey_date || project.created_at),
      'dd MMM yyyy'
    )}`

    if (
      confirm(`Are you sure you want to delete the upload to ${uploadName}?`)
    ) {
      this.props.apiClient.project.delete(project.id).then(() => {
        refetch()
        this.props.pushNotification({
          type: 'success',
          html: `Upload ${uploadName} deleted`,
        })
      })
    }
  }
}
